import type { ParcelLineItem } from "./parcelCreateTypes";
import {
  PARCEL_DESTINATION_ADDRESS_MAX_LENGTH,
  PARCEL_DESTINATION_FULLNAME_MAX_LENGTH,
} from "../manageParcelLayout";
import {
  canSearchParcelCarriers,
  isPackagingSelectionIncomplete,
} from "./parcelPackagingLogic";
import { calcTotalProductWeightGrams } from "./parcelCreateTypes";

export type ParcelDestinationFormValues = {
  fullname?: string;
  telephone1?: string;
  address?: string;
  province_id?: number;
  district_id?: number;
  sub_district_id?: number;
  postcode?: string;
  county?: string;
  city?: string;
  state?: string;
};

export function isParcelNameStepComplete(parcelName?: string): boolean {
  return !!parcelName?.trim();
}

export function isParcelProductsStepComplete(lines: ParcelLineItem[]): boolean {
  if (lines.length === 0) return false;
  return lines.some((l) => l.name?.trim());
}

export function isParcelPackagingStepComplete(params: {
  parentPackagingTypeId?: string;
  packagingTypeId?: string;
  showChildDropdown: boolean;
  boxWeight?: number;
  boxWidth?: number;
  boxLength?: number;
  boxHeight?: number;
  productLines: ParcelLineItem[];
}): boolean {
  const { showChildDropdown, productLines, ...rest } = params;
  if (
    isPackagingSelectionIncomplete(
      rest.parentPackagingTypeId,
      showChildDropdown,
      rest.packagingTypeId
    )
  ) {
    return false;
  }
  return canSearchParcelCarriers({
    packagingIncomplete: false,
    boxWeight: rest.boxWeight,
    boxWidth: rest.boxWidth,
    boxLength: rest.boxLength,
    boxHeight: rest.boxHeight,
    totalProductWeightGrams: calcTotalProductWeightGrams(productLines),
  });
}

export function isParcelAddressStepComplete(
  destination?: ParcelDestinationFormValues
): boolean {
  if (!destination) return false;
  const phone = String(destination.telephone1 ?? "").replace(/\D/g, "");
  const fullnameText = destination.fullname?.trim() ?? "";
  if (!fullnameText) return false;
  if (fullnameText.length > PARCEL_DESTINATION_FULLNAME_MAX_LENGTH) return false;
  if (!/^\d{10}$/.test(phone)) return false;
  const addressText = destination.address?.trim() ?? "";
  if (!addressText) return false;
  if (addressText.length > PARCEL_DESTINATION_ADDRESS_MAX_LENGTH) return false;
  if (!destination.province_id || !destination.district_id || !destination.sub_district_id) {
    return false;
  }
  if (!String(destination.postcode ?? "").trim()) return false;
  return !!(
    destination.county?.trim() &&
    destination.city?.trim() &&
    destination.state?.trim()
  );
}

export type ParcelCreateStepAccess = {
  products: boolean;
  packaging: boolean;
  address: boolean;
  carrier: boolean;
  others: boolean;
  footer: boolean;
};

export type ParcelCreateStepHints = {
  products?: string;
  packaging?: string;
  address?: string;
  carrier?: string;
  others?: string;
};

export function getParcelCreateStepAccess(params: {
  addressComplete: boolean;
  productsComplete: boolean;
  packagingComplete: boolean;
}): { enabled: ParcelCreateStepAccess; hints: ParcelCreateStepHints } {
  const { productsComplete, packagingComplete, addressComplete } = params;

  const productsUnlocked = addressComplete;
  const packagingUnlocked = addressComplete && productsComplete;
  const carrierUnlocked = packagingUnlocked && packagingComplete;

  const enabled: ParcelCreateStepAccess = {
    products: productsUnlocked,
    packaging: packagingUnlocked,
    address: true,
    carrier: carrierUnlocked,
    // others: carrierUnlocked, // ขั้นตอน 5 อื่นๆ — ปิดชั่วคราว
    others: false,
    footer: carrierUnlocked,
  };

  const hints: ParcelCreateStepHints = {};
  if (!addressComplete) {
    hints.products = "กรุณากรอกที่อยู่ผู้รับให้ครบในส่วนที่ 1 ก่อน";
    hints.packaging = hints.products;
    hints.carrier = hints.products;
    // hints.others = hints.products; // ขั้นตอน 5 อื่นๆ — ปิดชั่วคราว
  } else if (!productsComplete) {
    hints.packaging = "กรุณาระบุชื่อพัสดุในส่วนที่ 2 ก่อน";
    hints.carrier = hints.packaging;
    // hints.others = hints.packaging;
  } else if (!packagingComplete) {
    hints.carrier = "กรุณาเลือกบรรจุภัณฑ์และน้ำหนักให้ครบในส่วนที่ 2 ก่อน";
    // hints.others = hints.carrier;
  }

  return { enabled, hints };
}
